import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import Header from "../components/header/Header";
import ProgressBar from "../components/progressBar/ProgressBar";
import ReactFlagsSelect from "react-flags-select";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { FiArrowRight } from "react-icons/fi";
import FlowField from "../components/forms/FlowField";
import "./styles/flow-form.css";
import { addToCart } from "../redux/actions/CartActions";
import { useDispatch, useSelector } from "react-redux";
import { register, sendSignupOtp } from "../redux/actions/AuthUserActions";
import Swal from "sweetalert2";
import Loader from "../components/loader/Loader";

// Validation schema
const validationSchema = Yup.object({
  firstname: Yup.string().required("Le prénom est requis"),
  lastname: Yup.string().required("Le nom est requis"),
  email: Yup.string().email("Email invalide").required("L'email est requis"),
  country: Yup.string().required("Le pays est requis"),
  phoneNumber: Yup.string().required("Le numéro de téléphone est requis"),
  phoneWhatsapp: Yup.string().required("Le numéro WhatsApp est requis"),
  termsAccepted: Yup.boolean()
    .oneOf([true], "Vous devez accepter les conditions générales")
    .required("Vous devez accepter les conditions générales"),
});

function OwnerInformation() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const currentStep = "Propriétaire";
  const steps = [
    "Propriétaire",
    "Entreprise",
    "Services",
    "Paiement",
    "Finalisation",
  ];
  const [submitting, setSubmitting] = useState(false);
  const cart = useSelector((state) => state.cart);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const handleSubmit = (values) => {
    setSubmitting(true);

    dispatch(addToCart({ user: values }));

    dispatch(
      register(
        {
          ...values,
          mobile: values.phoneNumber,
          whatsApp: values.phoneWhatsapp,
        },
        (response) => handleCallbackRegister(response, values),
      ),
    );
  };

  const handleCallbackRegister = async (
    { success, message, conflict },
    values,
  ) => {
    const Toast = Swal.mixin({
      toast: true,
      position: "top-end",
      showConfirmButton: false,
      timer: 4000,
    });
    if (success) {
      await dispatch(
        sendSignupOtp({ email: values.email }, (response) =>
          handleCallbackAuth(response, values),
        ),
      );
    } else if (conflict) {
      await dispatch(
        sendSignupOtp({ email: values.email }, (response) =>
          handleCallbackAuth(response, values),
        ),
      );
    } else {
      Toast.fire({
        icon: "error",
        title: "Échec",
        text: message,
      });
      setSubmitting(false);
    }
  };

  const handleCallbackAuth = ({ success, message }, values) => {
    const Toast = Swal.mixin({
      toast: true,
      position: "top-end",
      showConfirmButton: false,
      timer: 4000,
    });
    if (success) {
      navigate("/otp", {
        state: { method: "email", email: values.email },
      });
    } else {
      Toast.fire({
        icon: "error",
        title: "Échec",
        text: message,
      });
    }
    setSubmitting(false);
  };

  const phoneField = (name, label, values, setFieldValue, errors, touched) => (
    <div className="flow-field">
      <label htmlFor={`field-${name}`} className="flow-label">
        {label}
      </label>
      <PhoneInput
        country={values.country.toLowerCase() || "cm"}
        value={values[name]}
        placeholder={label}
        onChange={(phone) =>
          setFieldValue(name, phone.startsWith("+") ? phone : `+${phone}`)
        }
        inputProps={{ id: `field-${name}` }}
        inputClass="flow-phone"
        containerClass="flow-phone-container"
      />
      {errors[name] && touched[name] && (
        <p className="flow-error">{errors[name]}</p>
      )}
    </div>
  );

  return (
    <div className="flow-form-page">
      <Loader isLoading={submitting} />
      <Header />
      <ProgressBar steps={steps} currentStep={currentStep} />

      <header className="flow-form-header">
        <h1 className="flow-form-title">
          Faisons <span>connaissance</span>
        </h1>
        <p className="flow-form-lead">
          Ces informations nous aident à vous accompagner et sont indispensables
          pour constituer votre dossier.
        </p>
      </header>

      <Formik
        initialValues={{
          firstname: cart.user?.firstname || "",
          lastname: cart.user?.lastname || "",
          email: cart.user?.email || "",
          country: cart.user?.country || "",
          phoneNumber: cart.user?.phoneNumber || "",
          phoneWhatsapp: cart.user?.phoneWhatsapp || "",
          termsAccepted: false,
        }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ setFieldValue, errors, touched, values }) => (
          <Form className="flow-form" noValidate>
            <div className="flow-form-row">
              <FlowField
                name="firstname"
                label="Prénom(s)"
                placeholder="Ex. : Marie"
                autoComplete="given-name"
              />
              <FlowField
                name="lastname"
                label="Nom(s)"
                placeholder="Ex. : Ngo Biyong"
                autoComplete="family-name"
              />
            </div>

            <FlowField
              name="email"
              type="email"
              label="E-mail"
              placeholder="vous@exemple.com"
              autoComplete="email"
            />

            <div className="flow-field">
              <span className="flow-label">Pays de résidence</span>
              <ReactFlagsSelect
                selected={values.country}
                countries={cart.from === "creation" ? ["CM"] : undefined}
                onSelect={(code) => setFieldValue("country", code)}
                placeholder="Choisissez votre pays"
                searchable
                searchPlaceholder="Recherchez votre pays"
                className="flow-flags"
              />
              {errors.country && touched.country && (
                <p className="flow-error">{errors.country}</p>
              )}
            </div>

            <div className="flow-form-row">
              {phoneField(
                "phoneNumber",
                "Téléphone",
                values,
                setFieldValue,
                errors,
                touched,
              )}
              {phoneField(
                "phoneWhatsapp",
                "WhatsApp",
                values,
                setFieldValue,
                errors,
                touched,
              )}
            </div>

            <div className="flow-check">
              <Field
                type="checkbox"
                name="termsAccepted"
                className="form-check-input"
                id="termsAccepted"
              />
              <label htmlFor="termsAccepted">
                J'atteste avoir lu et accepté les{" "}
                <Link to="/terms-and-conditions">Conditions générales</Link> et
                la{" "}
                <Link to="/privacy-policy">Politique de confidentialité</Link>.
              </label>
            </div>
            {errors.termsAccepted && touched.termsAccepted && (
              <p className="flow-error">{errors.termsAccepted}</p>
            )}

            <div className="flow-actions">
              <button
                type="submit"
                className="flow-submit"
                disabled={submitting}
              >
                Continuer <FiArrowRight aria-hidden="true" />
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
}

export default OwnerInformation;
